import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { toast } from "react-toastify";
import loginRequests from "../../../api/Customer/LoginRequests";
import { setUser } from "../../../redux/userSlice";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (location.state?.message) {
      toast.success(location.state.message);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const handleSubmit = async (
    values,
    { setSubmitting, setFieldError, setStatus }
  ) => {
    try {
      setStatus(null);

      const payload = {
        email: values.email,
        password: values.password,
      };

      const response = await loginRequests.login(payload);

      if (response.success) {
        const { data } = response;
        const userData = data.data?.data;
        const token = data.data?.data?.token;

        if (userData && token) {
          dispatch(
            setUser({
              userData,
              token,
            })
          );

          setLoginSuccess(true);
          toast.success(data.message || "Login successful! Welcome back!");

          setTimeout(() => {
            const redirectTo = location.state?.from || "/home";
            navigate(redirectTo, { replace: true });
          }, 1500);
        } else {
          throw new Error("Invalid response structure");
        }
      } else {
        const errorData = response.error;

        if (errorData.errors) {
          Object.keys(errorData.errors).forEach((field) => {
            const fieldMap = {
              email: "email",
              password: "password",
            };

            const formField = fieldMap[field] || field;
            setFieldError(formField, errorData.errors[field][0]);
          });
        } else if (errorData.message) {
          if (errorData.message.toLowerCase().includes("email")) {
            setFieldError("email", errorData.message);
          } else if (errorData.message.toLowerCase().includes("password")) {
            setFieldError("password", errorData.message);
          } else {
            setStatus(errorData.message);
            toast.error(errorData.message);
          }
        } else {
          setStatus(
            "Login failed. Please check your credentials and try again."
          );
          toast.error(
            "Login failed. Please check your credentials and try again."
          );
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setStatus("An unexpected error occurred. Please try again.");
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen md:flex-row">
      {/* Left section with info - hidden on mobile, fixed position on desktop */}
      <div
        className={`hidden md:flex w-2/5 bg-gradient-to-br from-red-600 to-red-500 text-white p-16 flex-col justify-between fixed h-screen overflow-hidden transition-opacity duration-500 ease-in-out ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
      >
        <div>
          <h1 className="mb-6 text-4xl font-bold">
            Welcome back to TXTConnect
          </h1>
          <p className="mb-8 text-lg opacity-90">
            Log in to access your messaging dashboard and connect with your
            audience.
          </p>

          <div className="mt-10 space-y-6">
            <div className="flex items-start space-x-3">
              <div className="p-2 rounded-full bg-white/20">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-medium">Bulk SMS</h3>
                <p className="text-sm opacity-80">
                  Send messages to thousands of recipients in one go
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="p-2 rounded-full bg-white/20">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-medium">Secure Platform</h3>
                <p className="text-sm opacity-80">
                  Your data and communications are always protected
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right section with login form - full width on mobile, adjusted for fixed sidebar on desktop */}
      <div
        className={`w-full md:w-3/5 p-8 md:p-16 md:ml-[40%] bg-white transition-transform duration-500 ease-in-out ${
          isVisible ? "translate-x-0" : "translate-x-8"
        }`}
      >
        <div className="mx-auto w-full max-w-md">
          {/* Mobile-only header with sign up link */}
          <div className="mb-8 text-center md:hidden">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="font-medium text-red-600 transition-all duration-300 hover:text-red-500"
                state={{ from: location.pathname }}
              >
                Sign up here
              </Link>
            </p>
          </div>

          <div
            className={`text-center md:text-left mb-10 transition-transform duration-500 ease-in-out ${
              isVisible ? "translate-y-0" : "translate-y-4"
            }`}
          >
            <h2 className="text-3xl font-bold text-gray-800">
              Log in to your account
            </h2>
            <p className="mt-2 text-gray-600">
              Enter your credentials to access your account
            </p>
          </div>

          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, status }) => (
              <Form
                className={`space-y-6 transition-opacity duration-700 ease-in-out ${
                  isVisible ? "opacity-100" : "opacity-0"
                }`}
              >
                {/* General error message */}
                {status && (
                  <div className="flex items-center p-4 space-x-2 bg-red-50 rounded-lg border border-red-200">
                    <AlertCircle className="flex-shrink-0 w-5 h-5 text-red-500" />
                    <p className="text-sm text-red-700">{status}</p>
                  </div>
                )}

                {/* Success message */}
                {loginSuccess && (
                  <div className="flex items-center p-4 space-x-2 bg-red-50 rounded-lg border border-red-200 animate-pulse">
                    <CheckCircle className="flex-shrink-0 w-5 h-5 text-red-500" />
                    <p className="text-sm text-red-700">
                      Login successful! Redirecting...
                    </p>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="email"
                    className="block mb-1 text-sm font-medium text-gray-700"
                  >
                    Email
                  </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Mail className="w-5 h-5 text-gray-400" />
                    </div>
                    <Field
                      type="email"
                      name="email"
                      id="email"
                      disabled={isSubmitting || loginSuccess}
                      className="block py-3 pl-10 w-full rounded-lg border border-gray-300 shadow-sm transition-all duration-300 outline-none focus:border-red-500 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                      placeholder="your@email.com"
                    />
                  </div>
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="flex items-center mt-1 space-x-1 text-sm text-red-600"
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block mb-1 text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                      <Lock className="w-5 h-5 text-gray-400" />
                    </div>
                    <Field
                      type={showPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      disabled={isSubmitting || loginSuccess}
                      className="block py-3 pl-10 w-full rounded-lg border border-gray-300 shadow-sm transition-all duration-300 outline-none focus:border-red-500 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isSubmitting || loginSuccess}
                      className="flex absolute inset-y-0 right-0 items-center pr-3 transition-all duration-300 disabled:opacity-50"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5 text-gray-400" />
                      ) : (
                        <Eye className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="flex items-center mt-1 space-x-1 text-sm text-red-600"
                  />
                </div>

                <div className="flex justify-between items-center">
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-red-600 transition-all duration-300 hover:text-red-500"
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || loginSuccess}
                  className="flex relative justify-center px-4 py-3 w-full font-medium text-white bg-gradient-to-r from-red-700 to-red-600 rounded-lg border border-transparent shadow-md transition-all duration-300 group hover:from-red-700 hover:to-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Logging in...
                    </>
                  ) : loginSuccess ? (
                    <>
                      <CheckCircle className="mr-2 w-5 h-5" />
                      Success!
                    </>
                  ) : (
                    <>
                      Log in
                      <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-150 group-hover:translate-x-1" />
                    </>
                  )}
                </button>

                <div className="mt-12 text-center">
                  <p className="text-sm opacity-80">
                    Don't have an account yet?{" "}
                    <Link
                      to="/signup"
                      className="font-medium text-red-600 underline transition-all duration-300 hover:text-red-500"
                      state={{ from: location.pathname }}
                    >
                      Sign up
                    </Link>
                  </p>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Login;
